import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BellRinging, ClockCounterClockwise, Lock } from "@phosphor-icons/react";

function ToggleCard({ icon: Icon, title, description, checked, onChange, busy, testId, active, readOnly }) {
  return (
    <div className={`border rounded-2xl p-6 transition-colors ${readOnly ? "bg-[#F4F7FB] border-[#E1E9F0] opacity-70" : active ? "bg-[#E6F1F5] border-[#C7DEE6]" : "bg-white border-[#E1E9F0]"}`}>
      <div className={`flex items-center justify-between gap-4 rounded-[14px] border p-4 ${readOnly ? "bg-[#EEF3F8] border-[#E1E9F0]" : "bg-white border-[#E1E9F0]"}`}>
        <div className="flex items-start gap-3 min-w-0">
          <Icon size={26} weight="duotone" className={`mt-0.5 shrink-0 ${readOnly ? "text-[#9AA9BC]" : "text-[#3A8FA5]"}`} />
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${readOnly ? "text-[#5F758F]" : "text-[#1E3A5F]"}`}>{title}</p>
            <p className="text-xs text-[#9AA9BC] mt-0.5">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 select-none">
          <span className={`text-[11px] tracking-[0.12em] uppercase font-semibold w-[80px] text-right ${readOnly ? "text-[#9AA9BC]" : active ? "text-[#3A8FA5]" : "text-[#9AA69F]"}`}>
            {active ? "Activées" : "Désactivées"}
          </span>
          {readOnly ? (
            <span
              className="inline-flex items-center h-7 w-12 rounded-full bg-[#E1E9F0] border border-[#D1D5DB] px-1 cursor-not-allowed"
              data-testid={testId}
              aria-readonly
              title="Réglage géré par le praticien du cabinet"
            >
              <span className={`h-5 w-5 rounded-full transition-transform bg-[#9AA9BC] ${active ? "translate-x-5" : "translate-x-0"}`} />
            </span>
          ) : (
            <label htmlFor={testId} className="cursor-pointer">
              <Switch
                id={testId}
                checked={checked}
                onCheckedChange={onChange}
                disabled={busy}
                className="h-7 w-12 data-[state=checked]:bg-[#3A8FA5] data-[state=unchecked]:bg-[#D1D5DB] [&>span]:h-6 [&>span]:w-6 [&>span]:data-[state=checked]:translate-x-5"
                data-testid={testId}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NotificationsPanel() {
  const { user, setUser } = useAuth();
  const isAdmin = (user?.role || "admin") === "admin";
  const [notifyOnSubmit, setNotifyOnSubmit] = useState(true);
  const [autoReminder, setAutoReminder] = useState(false);
  const [ownerName, setOwnerName] = useState(null);
  const [busy, setBusy] = useState(null); // "submit" | "reminder" | null

  useEffect(() => {
    if (!user) return;
    if (isAdmin) {
      setNotifyOnSubmit(user.notify_on_submit !== false);
      setAutoReminder(user.auto_reminder_enabled === true);
    } else {
      // Assistant / research: read-only mirror of the cabinet owner's flags.
      api.get("/cabinet/notification-settings").then(({ data }) => {
        setNotifyOnSubmit(!!data.notify_on_submit);
        setAutoReminder(!!data.auto_reminder_enabled);
        setOwnerName(data.owner_name);
      }).catch(() => {});
    }
  }, [user, isAdmin]);

  const patch = async (key, value, tag, labelOn, labelOff) => {
    if (!isAdmin) return;
    const prev = key === "notify_on_submit" ? notifyOnSubmit : autoReminder;
    (key === "notify_on_submit" ? setNotifyOnSubmit : setAutoReminder)(value);
    setBusy(tag);
    try {
      const { data } = await api.patch("/auth/me", { [key]: value });
      setUser(data);
      toast.success(value ? labelOn : labelOff);
    } catch (e) {
      (key === "notify_on_submit" ? setNotifyOnSubmit : setAutoReminder)(prev);
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  if (!user) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-1">Notifications</h2>
          <p className="text-sm text-[#5F758F]">
            {isAdmin
              ? "Choisissez les moments où Questiomed vous prévient par email, et automatisez les relances patients."
              : "Ces réglages sont gérés par le praticien titulaire du cabinet."}
          </p>
        </div>

        {!isAdmin && (
          <div className="rounded-[10px] bg-[#EEF3F8] border border-[#E1E9F0] px-4 py-3 flex items-start gap-2" data-testid="notifications-readonly-banner">
            <Lock size={14} weight="duotone" className="text-[#5F758F] shrink-0 mt-0.5" />
            <p className="text-[13px] text-[#5F758F] leading-relaxed">
              Réglages en lecture seule — définis par {ownerName ? <strong className="text-[#1E3A5F]">{ownerName}</strong> : "le praticien du cabinet"}.
            </p>
          </div>
        )}

        <ToggleCard
          icon={BellRinging}
          title="Nouvelle réponse patient"
          description="Recevez une notification par email à chaque fois qu'un patient complète un questionnaire."
          checked={notifyOnSubmit}
          onChange={(v) => patch("notify_on_submit", v, "submit", "Notifications de réponse activées", "Notifications de réponse désactivées")}
          busy={busy === "submit"}
          active={notifyOnSubmit}
          readOnly={!isAdmin}
          testId="notify-on-submit-toggle"
        />

        <ToggleCard
          icon={ClockCounterClockwise}
          title="Relances automatiques tous les 7 jours"
          description="Un email de rappel est envoyé automatiquement au patient tous les 7 jours tant qu'il n'a pas répondu au questionnaire."
          checked={autoReminder}
          onChange={(v) => patch("auto_reminder_enabled", v, "reminder", "Relances automatiques activées", "Relances automatiques désactivées")}
          busy={busy === "reminder"}
          active={autoReminder}
          readOnly={!isAdmin}
          testId="auto-reminder-toggle"
        />
      </div>

      <aside className="lg:col-span-1">
        <div className="bg-[#F4F7FB] border border-[#E1E9F0] rounded-2xl p-6 sticky top-24 space-y-3">
          <BellRinging size={26} weight="duotone" className="text-[#3A8FA5]" />
          <h3 className="text-xl font-bold tracking-tight text-[#1E3A5F]">À savoir</h3>
          <p className="text-sm text-[#5F758F] leading-relaxed">
            Les relances automatiques prennent le relais des rappels manuels : dès qu'un envoi ou un renvoi manuel a lieu, le compteur repart à 7 jours.
          </p>
          <p className="text-sm text-[#5F758F] leading-relaxed">
            Les questionnaires supprimés et ceux déjà complétés ne reçoivent plus de rappel.
          </p>
        </div>
      </aside>
    </div>
  );
}
